import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchReferralInfo,
  fetchTeamMembers,
  fetchRewards,
  fetchUnclaimedRewards,
  claimAllRewards,
  calculateRewardsStats,
} from '@/services/api/affiliateService';
import { useToast } from '@/hooks';
import storage from '@/utils/storage';
import CommissionHistory from './History/CommissionHistory';
import ReferralHistory from './History/ReferralHistory';
import styles from './AffiliateRewards.module.css';
import homeStyles from '../../Home/Home.module.css';

/**
 * AffiliateRewards Component
 * Optimized with React Query for fast data fetching and caching
 */
const AffiliateRewards = () => {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [isHistoryTab, setIsHistoryTab] = useState('Commission');

  // Check if user is logged in - use storage utility (stable value)
  const isLoggedIn = React.useMemo(() => {
    try {
      const token = storage.getKey('token');
      return !!token;
    } catch {
      return false;
    }
  }, []); // Empty deps - only check once on mount

  // Fetch referral info (cached for 5 minutes) - only if logged in
  const {
    data: referralInfo,
    isLoading: isLoadingReferral,
    error: referralError,
  } = useQuery({
    queryKey: ['affiliate', 'referralInfo'],
    queryFn: fetchReferralInfo,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Reduced retries
    enabled: isLoggedIn, // Only fetch if user is logged in
    refetchOnWindowFocus: false, // Prevent refetch on focus
    refetchOnMount: false, // Prevent refetch on mount if data exists
    refetchOnReconnect: false, // Prevent refetch on reconnect
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  // Get referral code (stable value to prevent query key changes)
  const referralCode = referralInfo?.referralCode || '';

  // Fetch team members (only if referral code exists and is valid)
  const {
    data: teamData,
    isLoading: isLoadingTeam,
  } = useQuery({
    queryKey: ['affiliate', 'team', referralCode],
    queryFn: () => fetchTeamMembers(referralCode),
    enabled: !!referralCode && referralCode.length > 0 && !!isLoggedIn,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  // Fetch rewards (only if referral code exists and is valid)
  const {
    data: rewardsData,
    isLoading: isLoadingRewards,
  } = useQuery({
    queryKey: ['affiliate', 'rewards', referralCode],
    queryFn: () => fetchRewards(referralCode),
    enabled: !!referralCode && referralCode.length > 0 && !!isLoggedIn,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    gcTime: 10 * 60 * 1000,
  });

  // Fetch unclaimed rewards - only if logged in
  const {
    data: unclaimedData,
    isLoading: isLoadingUnclaimed,
  } = useQuery({
    queryKey: ['affiliate', 'unclaimedRewards'],
    queryFn: fetchUnclaimedRewards,
    staleTime: 2 * 60 * 1000, // 2 minutes (more frequent for unclaimed)
    retry: 1,
    enabled: isLoggedIn, // Only fetch if user is logged in
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    gcTime: 5 * 60 * 1000,
  });

  // Calculate stats with memoization
  const stats = useMemo(() => {
    if (!rewardsData || !teamData) {
      return {
        totalMembers: 0,
        totalRewards: 0,
        referralRewards: 0,
        commissionRewards: 0,
        totalReferrals: 0,
      };
    }

    return calculateRewardsStats(
      rewardsData.rewards,
      teamData.teamMembers?.length || 0,
      rewardsData.totalRewards || 0
    );
  }, [rewardsData, teamData]);

  // Claim rewards mutation
  const claimMutation = useMutation({
    mutationFn: claimAllRewards,
    onSuccess: (data) => {
      if (data.success) {
        showSuccess(data.message || 'Withdraw successful!');
        // Invalidate and refetch all affiliate queries
        queryClient.invalidateQueries({ queryKey: ['affiliate'] });
      } else {
        showError(data.message || 'Withdraw failed');
      }
    },
    onError: (error) => {
      console.error('Error during withdrawal:', error);
      showError('An error occurred during withdrawal. Please try again later.');
    },
  });

  const handleTabClick = (tab) => {
    setIsHistoryTab(tab);
  };

  const handleWithdrawToWallet = () => {
    claimMutation.mutate();
  };

  // Combined loading state - only show if actually loading and logged in
  // const isInitialLoading = isLoggedIn && (
  //   isLoadingReferral || 
  //   (referralInfo?.referralCode && (isLoadingTeam || isLoadingRewards || isLoadingUnclaimed))
  // );

  // Show loader while initial data is loading (only if logged in)
  // if (isInitialLoading) {
  //   return (
  //     <div className={styles.wrapper}>
  //       <div className={homeStyles.loadingContainer}>
  //         <div className={homeStyles.loader}>
  //           <span className={homeStyles.loaderText}>loading</span>
  //           <span className={homeStyles.load}></span>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  // If not logged in, show message
  if (!isLoggedIn) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.errorState}>
          <p>Please log in to view your rewards.</p>
        </div>
      </div>
    );
  }

  // Show error state only if there's an actual error and we're logged in
  if (referralError && isLoggedIn) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.errorState}>
          <p>Error loading rewards data. Please try again later.</p>
        </div>
      </div>
    );
  }

  const totalUnclaimed = unclaimedData?.totalUnclaimed || 0;

  // Show loading indicator in header if initial data is still loading
  const isDataLoading = isLoadingReferral || (referralCode && (isLoadingTeam || isLoadingRewards || isLoadingUnclaimed));

  return (
    <div className={styles.body}>
      <div className={styles.sectionHeader}>
        <div className={styles.column}>
          <div>
            <span className={styles.label}>Total Commission Rewards</span>
          </div>
          <div>
            {isDataLoading ? (
              <span className={styles.valueText}>Loading...</span>
            ) : (
              <span className={styles.valueText}>{stats.commissionRewards.toFixed(2)} JKP</span>
            )}
          </div>
        </div>

        <div className={styles.column}>
          <div>
            <span className={styles.label}>Available Referral Rewards</span>
          </div>
          <div>
            {isDataLoading ? (
              <span className={styles.valueText}>Loading...</span>
            ) : (
              <span className={styles.valueText}>{totalUnclaimed.toFixed(2)} JKP</span>
            )}
          </div>
          <div className={styles.flexRow}>
            <div className={styles.flexRow}>
              <span className={styles.label}>Total Received:</span>
              {isDataLoading ? (
                <span className={`${styles.label} ${styles.bold}`}>Loading...</span>
              ) : (
                <span className={`${styles.label} ${styles.bold}`}>{stats.totalReferrals.toFixed(2)} JKP</span>
              )}
            </div>
            <div className={styles.flexRow} style={{ marginLeft: '5px' }}>
              <span className={styles.label}>Locked:</span>
              {isDataLoading ? (
                <span className={`${styles.label} ${styles.dimmed} ${styles.bold}`}>Loading...</span>
              ) : (
                <span className={`${styles.label} ${styles.dimmed} ${styles.bold}`}>
                  {stats.referralRewards.toFixed(2)} JKP
                </span>
              )}
            </div>
          </div>
        </div>

        <div className={styles.dividerContainer}>
          <button
            className={styles.withdrawButton}
            onClick={handleWithdrawToWallet}
            disabled={claimMutation.isPending}
          >
            {claimMutation.isPending ? 'Processing...' : 'Withdraw to Wallet'}
          </button>
        </div>
      </div>

      <div className={styles.sectionBody}>
        <div className={styles.sectionNavWrapper}>
          <div className={styles.navItems}>
            <button
              className={`${styles.navItem} ${isHistoryTab === 'Commission' ? styles.active : ''}`}
              onClick={() => handleTabClick('Commission')}
            >
              Commission
            </button>
            <button
              className={`${styles.navItem} ${isHistoryTab === 'Referral' ? styles.active : ''}`}
              onClick={() => handleTabClick('Referral')}
            >
              Referral
            </button>
          </div>

          <div className={styles.rulesLink}>
            <Link to="/affiliate/rules" className={styles.rulesLinkText}>
              Rules
            </Link>
          </div>
        </div>

        <div className={styles.sectionDataWrapper}>
          {isHistoryTab === 'Commission' && <CommissionHistory referralCode={referralCode} />}
          {isHistoryTab === 'Referral' && <ReferralHistory referralCode={referralCode} />}
        </div>
      </div>
    </div>
  );
};

export default AffiliateRewards;
